/**
 * Single source of truth for QA alert category metadata.
 *
 * Every rendering surface (badge, widget, calendar legend, detail view) must
 * derive its label, color token, and calendar symbol from this config — no
 * surface should hard-code its own copy. This prevents the label/color
 * divergence described in REBUILD-147 / E22-S9.
 *
 * Color token policy: each category maps to a UNIQUE core-palette hue (see
 * `shared/constants/statusPalette.ts`). All color classes are DERIVED from that
 * palette — never hand-written here — so a hue change propagates automatically.
 */

import type { TranslationKey } from '@/shared/lib/i18n'
import { STATUS_PALETTE, badgeClassFor, type PaletteHue } from '@/shared/constants/statusPalette'

export type AlertCategoryKey =
  | 'nonCompliant'
  | 'failures'
  | 'dateChanges'
  | 'serviceReports'
  | 'correctiveActions'
  | 'statusChanges'
  | 'departmentMoved'

export interface AlertCategoryConfig {
  /**
   * Stable identifier. Monthly AlertCounts fields use the first five keys;
   * `statusChanges` / `departmentMoved` are Maintenance Logs calendar KPIs only.
   */
  key: AlertCategoryKey
  /**
   * Canonical i18n key — must be a valid TranslationKey.
   * Use `t(config.labelKey)` at the render site.
   */
  labelKey: TranslationKey
  /**
   * The core-palette hue this category maps to. Colors below are DERIVED from
   * `STATUS_PALETTE[hue]` — never hand-written — so a hue change in the palette
   * propagates here automatically. See `shared/constants/statusPalette.ts`.
   */
  hue: PaletteHue
  /**
   * Tailwind class string for the badge variant (tint bg + deep text + border).
   * Derived from the hue via `badgeClassFor`. Each category maps to a UNIQUE hue.
   */
  badgeColorClass: string
  /** Tailwind text-color class for the dot used in the calendar legend (core step). */
  dotColorClass: string
  /** Tailwind bg-color class for the dot used in the trend chart (core step). */
  dotBgColorClass: string
}

/**
 * Hue assignment per the NextGen Status Color Standard §05:
 *   nonCompliant → red · failures → orange · dateChanges → yellow
 *   serviceReports → grey (a vendor visit is a record, not an open exception)
 *   correctiveActions → purple · statusChanges → indigo · departmentMoved → fuchsia
 * indigo/fuchsia are the Scale-B-only category palette (outside the seven cores).
 */
const buildCategory = (
  key: AlertCategoryKey,
  labelKey: TranslationKey,
  hue: PaletteHue,
): AlertCategoryConfig => ({
  key,
  labelKey,
  hue,
  badgeColorClass: badgeClassFor(hue),
  dotColorClass: STATUS_PALETTE[hue].coreText,
  dotBgColorClass: STATUS_PALETTE[hue].coreBg,
})

/** Ordered from most severe to least severe (matches display order). */
export const QA_ALERT_CATEGORIES: AlertCategoryConfig[] = [
  buildCategory('nonCompliant', 'reports.monthly.alerts.nonCompliant', 'red'),
  buildCategory('failures', 'reports.monthly.alerts.failures', 'orange'),
  buildCategory('dateChanges', 'reports.monthly.alerts.dateChanges', 'yellow'),
  buildCategory('serviceReports', 'reports.monthly.alerts.serviceReports', 'grey'),
  buildCategory('correctiveActions', 'reports.monthly.alerts.correctiveActions', 'purple'),
  buildCategory('statusChanges', 'reports.monthly.alerts.statusChanges', 'indigo'),
  buildCategory('departmentMoved', 'reports.monthly.alerts.departmentMoved', 'fuchsia'),
]

/**
 * Categories rendered on the monthly report alert bar / trend (backend AlertCounts).
 * Status/Dept are Maintenance Logs calendar KPIs only — not monthly alert counts.
 */
export const MONTHLY_REPORT_ALERT_CATEGORY_KEYS: ReadonlySet<AlertCategoryKey> = new Set([
  'nonCompliant',
  'failures',
  'dateChanges',
  'serviceReports',
  'correctiveActions',
])

export const QA_ALERT_CATEGORY_MAP: Record<AlertCategoryKey, AlertCategoryConfig> =
  Object.fromEntries(QA_ALERT_CATEGORIES.map((config) => [config.key, config])) as Record<
    AlertCategoryKey,
    AlertCategoryConfig
  >

/**
 * Deep-step text token for the category's hue (e.g. text-yellow-800) for
 * icons/labels on tinted backgrounds. Prefer over dotColorClass (core step)
 * when WCAG contrast on cell fills matters (calendar date-modified check,
 * log-table date-modified suffix).
 */
export const alertCategoryEmphasisTextClass = (categoryKey: AlertCategoryKey): string =>
  STATUS_PALETTE[QA_ALERT_CATEGORY_MAP[categoryKey].hue].deepText
